import { useCallback, useEffect, useState } from 'react'
import { useOverlay } from '@/shared/lib/overlay/useOverlay'
import { useSessionStore } from '@/features/session/model/sessionStore'
import { useTheme, type ThemeMode } from '@/shared/lib/useTheme'
import { useAppPermissions } from '@/shared/lib/useAppPermissions'
import { applyPhoneMask, getPhoneDigits } from '@/shared/lib/phoneMask'
import { parseSpecialtiesText } from '@/shared/lib/profileFormUtils'
import { TextInput } from '@/shared/ui/TextInput'
import { MonitorIcon, MoonIcon, SunIcon } from '@/shared/ui/icons'
import { cn } from '@/shared/lib/cn'

interface OnboardingStep {
  icon: string
  title: string
  description: string
  color: string
  /** Texto introdutório opcional (ex.: "O que você encontra no app"). */
  intro?: string
}

const TOUR_STEPS: OnboardingStep[] = [
  {
    icon: '🩺',
    title: 'Prontuário completo',
    description:
      'Cadastre pacientes, registre exames, documentos clínicos e atendimentos. Acompanhe a evolução com gráficos e timeline por paciente.',
    color: 'var(--color-teal-ink)',
    intro: 'O que você encontra no app',
  },
  {
    icon: '🤖',
    title: 'IA que entende você',
    description:
      'Converse em linguagem natural. Pergunte sobre pacientes, peça resumos, sugira condutas ou crie registros por voz. Microfone em vários campos.',
    color: 'var(--color-hub-indigo)',
  },
  {
    icon: '📄',
    title: 'Documentos e receitas',
    description:
      'Receitas, atestados, solicitações e modelos personalizados. Gere PDF com logo, assinatura e CRM. Imprima ou compartilhe.',
    color: 'var(--color-hub-cyan)',
  },
  {
    icon: '📅',
    title: 'Agenda e calendário',
    description:
      'Visualize e gerencie compromissos. Sincronize com Google Calendar, crie eventos manualmente e mantenha tudo organizado.',
    color: 'var(--color-hub-violet)',
  },
  {
    icon: '🧮',
    title: 'Doses Pré-calculadas',
    description:
      'Calcule dose, volume e diluição de medicamentos. Use voz para preencher ou salve doses na lista para reutilizar depois.',
    color: 'var(--color-hub-amber)',
  },
  {
    icon: '📊',
    title: 'Importação inteligente',
    description:
      'Envie fotos ou PDFs de exames e a IA extrai os dados. Importe pacientes, CID e procedimentos em lote com apoio da IA.',
    color: 'var(--color-hub-emerald)',
  },
]

const THEME_OPTIONS: Array<{ value: ThemeMode; label: string; icon: typeof SunIcon }> = [
  { value: 'light', label: 'Claro', icon: SunIcon },
  { value: 'dark', label: 'Escuro', icon: MoonIcon },
  { value: 'system', label: 'Sistema', icon: MonitorIcon },
]

interface OnboardingWelcomeProps {
  userName?: string
  onComplete: () => void
}

export const OnboardingWelcome = ({ userName, onComplete }: OnboardingWelcomeProps) => {
  const user = useSessionStore((s) => s.user)
  const role = useSessionStore((s) => s.user?.role)
  const visibleTourSteps =
    role === 'patient' ? TOUR_STEPS.filter((_, i) => [0, 1, 3].includes(i)) : TOUR_STEPS
  const updateProfile = useSessionStore((s) => s.updateProfile)
  const [currentStep, setCurrentStep] = useState(0)

  const { mode: themeMode, setMode: setThemeMode, ready: themeReady } = useTheme()
  const {
    camera,
    microphone,
    notifications,
    refresh: refreshPermissions,
    requestCamera,
    requestMicrophone,
    hasCamera,
    hasMicrophone,
    hasNotifications,
    permissionSheet,
  } = useAppPermissions()

  const [displayName, setDisplayName] = useState('')
  const [crm, setCrm] = useState('')
  const [specialtiesText, setSpecialtiesText] = useState('')
  const [phone1, setPhone1] = useState('')

  const [camChoice, setCamChoice] = useState<'later' | 'now'>('later')
  const [micChoice, setMicChoice] = useState<'later' | 'now'>('later')
  const [notifChoice, setNotifChoice] = useState<'later' | 'now'>('later')

  const totalSteps = 1 + visibleTourSteps.length
  const isProfileStep = currentStep === 0
  const tourStep = !isProfileStep ? visibleTourSteps[currentStep - 1] : null
  const isLast = currentStep === totalSteps - 1
  const stepColor = isProfileStep ? 'var(--color-teal-ink)' : tourStep!.color

  useEffect(() => {
    if (!user) return
    // Hidrata o formulário quando o store de sessão traz o perfil.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- valores iniciais dependem do user assíncrono
    setDisplayName(user.name ?? '')
    setCrm(user.crm ?? '')
    setSpecialtiesText((user.specialties ?? user.especialidades ?? []).join(', '))
    setPhone1(applyPhoneMask(user.phone1 ?? user.telefone1 ?? ''))
  }, [user])

  const camGranted = camera === 'granted'
  const micGranted = microphone === 'granted'
  const notifGranted = notifications === 'granted'

  const saveProfileSnapshot = useCallback(async () => {
    if (!user) return
    const digits = getPhoneDigits(phone1)
    const phonePayload = digits.length >= 10 ? applyPhoneMask(phone1).trim() : undefined
    if (user.role === 'doctor') {
      await updateProfile({
        name: displayName.trim() || user.name,
        crm: crm.trim() || undefined,
        specialties: parseSpecialtiesText(specialtiesText),
        phone1: phonePayload ?? user.phone1,
      })
    } else {
      await updateProfile({
        name: displayName.trim() || user.name,
        phone1: phonePayload ?? user.phone1,
        specialties: user.specialties ?? user.especialidades ?? [],
      })
    }
  }, [user, displayName, crm, specialtiesText, phone1, updateProfile])

  const handleThemeSelect = useCallback(
    async (mode: ThemeMode) => {
      await setThemeMode(mode)
    },
    [setThemeMode],
  )

  const handleNotifNow = useCallback(async () => {
    if (typeof Notification === 'undefined') return
    await Notification.requestPermission()
    refreshPermissions()
  }, [refreshPermissions])

  const handleNext = useCallback(() => {
    if (isLast) {
      onComplete()
    } else {
      setCurrentStep((prev) => prev + 1)
    }
  }, [isLast, onComplete])

  // Passos do tour (depois do perfil) entram na pilha única: voltar (web e nativo) e Esc recuam
  // um passo em vez de sair da tela de boas-vindas. No 1º passo o voltar segue o padrão da rota.
  useOverlay(currentStep > 0, {
    kind: 'dialog',
    label: 'Boas-vindas',
    restoreFocus: false,
    onRequestClose: (reason) => {
      if (reason === 'back' || reason === 'escape') setCurrentStep((prev) => Math.max(0, prev - 1))
    },
  })

  const handleSkip = useCallback(() => {
    onComplete()
  }, [onComplete])

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-[var(--color-page)] py-6">
      {permissionSheet}
      <div className="mx-auto flex w-full max-w-md flex-col items-center px-6 py-4">
        <div
          className="mb-5 flex h-24 w-24 items-center justify-center rounded-3xl text-5xl shadow-lg transition-all duration-500 sm:h-28 sm:w-28"
          style={{
            backgroundColor: `${stepColor}18`,
            boxShadow: `0 8px 32px ${stepColor}22`,
          }}
        >
          {isProfileStep ? '✨' : tourStep?.icon}
        </div>

        {isProfileStep ? (
          <>
            {userName ? (
              <p className="mb-2 text-sm font-medium text-[var(--color-text-muted)]">
                Olá, {userName.split(' ')[0]}! 👋
              </p>
            ) : null}
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
              Personalize o Synap
            </p>
            <h1
              className="mb-3 text-center text-xl font-bold sm:text-2xl"
              style={{ color: stepColor }}
            >
              Tema e perfil
            </h1>
            <div className="mb-6 w-full space-y-4 text-left">
              <div>
                <p className="mb-2 text-xs font-semibold text-[var(--color-text-secondary)]">Tema</p>
                <div className="flex flex-wrap gap-2">
                  {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      disabled={!themeReady}
                      onClick={() => void handleThemeSelect(value)}
                      className={cn(
                        'flex flex-1 min-w-[5.5rem] items-center justify-center gap-1.5 rounded-xl border px-2 py-2 text-xs font-semibold transition-colors sm:min-w-0 sm:flex-1',
                        themeMode === value
                          ? 'border-[var(--color-mint-strong)] bg-[var(--color-mint-soft)] text-[var(--color-teal-ink)]'
                          : 'border-[var(--color-border)] bg-[var(--color-page)] text-[var(--color-text-primary)]',
                      )}
                    >
                      <Icon size={16} aria-hidden />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <TextInput
                label="Nome de exibição"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                onBlur={() => {
                  if (displayName.trim()) void saveProfileSnapshot()
                }}
              />

              {user?.role === 'doctor' ? (
                <>
                  <TextInput
                    label="CRM"
                    value={crm}
                    onChange={(e) => setCrm(e.target.value)}
                    onBlur={() => void saveProfileSnapshot()}
                  />
                  <div>
                    <TextInput
                      label="Especialidades"
                      value={specialtiesText}
                      onChange={(e) => setSpecialtiesText(e.target.value)}
                      onBlur={() => void saveProfileSnapshot()}
                    />
                    <p className="mt-1 text-[10px] text-[var(--color-text-muted)]">
                      Separe por vírgula (ex.: Clínica Geral, Pediatria).
                    </p>
                  </div>
                </>
              ) : null}

              <div title="Formato: (DDD) número — celular ou fixo">
                <TextInput
                  label="Contato (telefone)"
                  value={phone1}
                  inputMode="tel"
                  autoComplete="tel"
                  onChange={(e) => setPhone1(applyPhoneMask(e.target.value))}
                  onBlur={() => void saveProfileSnapshot()}
                />
              </div>

              <div className="space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-page)]/80 p-3">
                <p className="text-xs font-semibold text-[var(--color-text-primary)]">
                  Permissões do dispositivo
                </p>
                <p className="text-[11px] text-[var(--color-text-secondary)]">
                  Escolha se deseja conceder agora ou ajustar depois em Configurações → Meu perfil → Permissões do app.
                </p>

                {hasCamera ? (
                  <fieldset className="space-y-1">
                    <legend className="text-xs font-medium text-[var(--color-text-secondary)]">
                      Câmera — digitalizar documentos e códigos
                    </legend>
                    <label className="flex cursor-pointer items-center gap-2 text-xs">
                      <input
                        type="radio"
                        name="onb-cam"
                        checked={!camGranted && camChoice === 'later'}
                        onChange={() => setCamChoice('later')}
                        className="accent-[var(--color-mint-strong)]"
                      />
                      Configurar depois
                    </label>
                    <label className="flex cursor-pointer items-center gap-2 text-xs">
                      <input
                        type="radio"
                        name="onb-cam"
                        checked={camGranted || camChoice === 'now'}
                        onChange={() => {
                          setCamChoice('now')
                          void requestCamera()
                        }}
                        className="accent-[var(--color-mint-strong)]"
                      />
                      Permitir agora
                    </label>
                  </fieldset>
                ) : null}

                {hasMicrophone ? (
                  <fieldset className="space-y-1">
                    <legend className="text-xs font-medium text-[var(--color-text-secondary)]">
                      Microfone — ditado de queixas, condutas e IA
                    </legend>
                    <label className="flex cursor-pointer items-center gap-2 text-xs">
                      <input
                        type="radio"
                        name="onb-mic"
                        checked={!micGranted && micChoice === 'later'}
                        onChange={() => setMicChoice('later')}
                        className="accent-[var(--color-mint-strong)]"
                      />
                      Configurar depois
                    </label>
                    <label className="flex cursor-pointer items-center gap-2 text-xs">
                      <input
                        type="radio"
                        name="onb-mic"
                        checked={micGranted || micChoice === 'now'}
                        onChange={() => {
                          setMicChoice('now')
                          void requestMicrophone()
                        }}
                        className="accent-[var(--color-mint-strong)]"
                      />
                      Permitir agora
                    </label>
                  </fieldset>
                ) : null}

                {hasNotifications ? (
                  <fieldset className="space-y-1">
                    <legend className="text-xs font-medium text-[var(--color-text-secondary)]">
                      Notificações — lembretes da agenda
                    </legend>
                    <label className="flex cursor-pointer items-center gap-2 text-xs">
                      <input
                        type="radio"
                        name="onb-notif"
                        checked={!notifGranted && notifChoice === 'later'}
                        onChange={() => setNotifChoice('later')}
                        className="accent-[var(--color-mint-strong)]"
                      />
                      Configurar depois
                    </label>
                    <label className="flex cursor-pointer items-center gap-2 text-xs">
                      <input
                        type="radio"
                        name="onb-notif"
                        checked={notifGranted || notifChoice === 'now'}
                        onChange={() => {
                          setNotifChoice('now')
                          void handleNotifNow()
                        }}
                        className="accent-[var(--color-mint-strong)]"
                      />
                      Permitir agora
                    </label>
                  </fieldset>
                ) : null}
              </div>

              <p className="text-[11px] leading-relaxed text-[var(--color-text-muted)]">
                Você pode revisar tema, dados profissionais e permissões a qualquer momento em{' '}
                <strong>Configurações</strong> → <strong>Meu perfil</strong> e demais opções de configuração.
              </p>
            </div>
          </>
        ) : (
          <>
            {tourStep?.intro && (
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-[var(--color-text-muted)]">
                {tourStep.intro}
              </p>
            )}

            <h1
              className="mb-3 text-center text-2xl font-bold transition-all duration-300"
              style={{ color: stepColor }}
            >
              {tourStep?.title}
            </h1>

            <p className="mb-8 max-w-xs text-center text-sm leading-relaxed text-[var(--color-text-secondary)]">
              {tourStep?.description}
            </p>
          </>
        )}

        <div className="mb-6 flex max-w-full flex-wrap justify-center gap-2">
          {Array.from({ length: totalSteps }).map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => setCurrentStep(idx)}
              className="h-2 rounded-full transition-all duration-300"
              style={{
                width: idx === currentStep ? 24 : 8,
                backgroundColor: idx === currentStep ? stepColor : 'var(--color-border)',
              }}
              aria-label={`Etapa ${idx + 1}`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={handleNext}
          className="mb-3 flex h-12 w-full max-w-xs items-center justify-center rounded-xl text-sm font-semibold text-white shadow-md transition-all duration-200 hover:shadow-lg active:scale-[0.98]"
          style={{ backgroundColor: stepColor }}
        >
          {isLast ? 'Começar a usar' : 'Próximo'}
        </button>

        {!isLast && (
          <button
            type="button"
            onClick={handleSkip}
            className="text-xs font-medium text-[var(--color-text-muted)] transition-colors hover:text-[var(--color-text-primary)]"
          >
            Pular introdução
          </button>
        )}
      </div>
    </div>
  )
}